import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

// Import components
import WelcomeScreen from './WelcomeScreen';
import PhotoCapture from './PhotoCapture';
import UserInfoForm from './UserInfoForm';
import DocsAnalysis from './DocsAnalysis';

function DocsApp() {
  const navigate = useNavigate();
  const location = useLocation();

  // Docs-specific application state
  const [docsAppState, setDocsAppState] = useState({
    currentStep: 0,
    patientData: null,
    capturedImage: null,
    analysisResult: null,
    selectedDoctor: null,
    userLocation: null,
    prefetchedDoctors: null,
    isLoading: false,
    error: null
  });

  // Handle URL-based step navigation
  useEffect(() => {
    const pathMap = {
      '/docs': 0,
      '/docs/photo-capture': 1,
      '/docs/patient-info': 2,
      '/docs/analysis': 3
    };

    const currentStep = pathMap[location.pathname];
    if (currentStep !== undefined && currentStep !== docsAppState.currentStep) {
      setDocsAppState(prev => ({ ...prev, currentStep }));
    }
  }, [location.pathname, docsAppState.currentStep]);

  // Debug logging

  // Navigation functions for docs
  const docsNextStep = () => {
    const routes = ['/docs', '/docs/photo-capture', '/docs/patient-info', '/docs/analysis'];
    const nextStep = docsAppState.currentStep + 1;
    if (nextStep < routes.length) {
      setDocsAppState(prev => ({ ...prev, currentStep: nextStep, error: null }));
      navigate(routes[nextStep]);
    }
  };

  const docsPreviousStep = () => {
    const routes = ['/docs', '/docs/photo-capture', '/docs/patient-info', '/docs/analysis'];
    const prevStep = docsAppState.currentStep - 1;
    if (prevStep >= 0) {
      setDocsAppState(prev => ({ ...prev, currentStep: prevStep, error: null }));
      navigate(routes[prevStep]);
    }
  };

  const docsGoToStep = (stepIndex) => {
    const routes = ['/docs', '/docs/photo-capture', '/docs/patient-info', '/docs/analysis'];
    if (stepIndex >= 0 && stepIndex < routes.length) {
      setDocsAppState(prev => ({ ...prev, currentStep: stepIndex, error: null }));
      navigate(routes[stepIndex]);
    }
  };

  const docsResetApp = () => {
    setDocsAppState({
      currentStep: 0,
      patientData: null,
      capturedImage: null,
      analysisResult: null,
      selectedDoctor: null,
      userLocation: null,
      prefetchedDoctors: null,
      isLoading: false,
      error: null
    });
    navigate('/docs');
  };

  // Data update functions for docs
  const docsUpdatePatientData = (data) => {
    setDocsAppState(prev => ({ ...prev, patientData: { ...prev.patientData, ...data } }));
  };

  const docsUpdateCapturedImage = (imageData) => {
    setDocsAppState(prev => ({ ...prev, capturedImage: imageData }));
  };

  const docsUpdateAnalysisResult = (result) => {
    setDocsAppState(prev => ({ ...prev, analysisResult: result }));
  };

  const docsUpdateSelectedDoctor = (doctor) => {
    setDocsAppState(prev => ({ ...prev, selectedDoctor: doctor }));
  };

  const docsSetLoading = (loading) => {
    setDocsAppState(prev => ({ ...prev, isLoading: loading }));
  };

  const docsSetError = (error) => {
    setDocsAppState(prev => ({ ...prev, error, isLoading: false }));
  };

  const docsUpdateUserLocation = (coords) => {
    if (!coords) return;
    const next = coords.lat != null && coords.lng != null
      ? { lat: coords.lat, lng: coords.lng, accuracy: coords.accuracy, source: coords.source || 'gps' }
      : (coords.latitude != null && coords.longitude != null
          ? { lat: coords.latitude, lng: coords.longitude, accuracy: coords.accuracy, source: coords.source || 'gps' }
          : null);
    if (next) {
      setDocsAppState(prev => ({ ...prev, userLocation: next }));
    }
  };

  // Check if step is accessible for docs
  const docsIsStepAccessible = (stepIndex) => {
    switch (stepIndex) {
      case 0: return true; // Welcome always accessible
      case 1: return true; // Photo capture always accessible after welcome
      case 2: return docsAppState.capturedImage !== null; // User info needs image
      case 3: return docsAppState.patientData !== null && docsAppState.capturedImage !== null; // Analysis needs both
      default: return false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="min-h-screen">
        <Routes>
          <Route
            path=""
            element={
              <WelcomeScreen
                appState={docsAppState}
                nextStep={docsNextStep}
              />
            }
          />

          <Route
            path="photo-capture"
            element={
              <PhotoCapture
                appState={docsAppState}
                nextStep={docsNextStep}
                updateCapturedImage={docsUpdateCapturedImage}
                setError={docsSetError}
              />
            }
          />

          <Route
            path="patient-info"
            element={
              <UserInfoForm
                appState={docsAppState}
                nextStep={docsNextStep}
                previousStep={docsPreviousStep}
                updatePatientData={docsUpdatePatientData}
                setError={docsSetError}
              />
            }
          />

          <Route
            path="analysis"
            element={
              <DocsAnalysis
                appState={docsAppState}
                nextStep={docsNextStep}
                previousStep={docsPreviousStep}
                goToStep={docsGoToStep}
                updateAnalysisResult={docsUpdateAnalysisResult}
                setLoading={docsSetLoading}
                setError={docsSetError}
              />
            }
          />

          <Route path="*" element={<Navigate to="/docs" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default DocsApp;
